import React, { useState } from 'react';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';

// ============================================================
// RailEurope.tsx
//
// Tourism-focused rewrite: leads with the scenery, not the
// logistics. Real, distinct photography for each scenic route,
// a journeys/packages section, and an onboard-comfort panel —
// same teal / gold / cream identity as the rest of the site.
// ============================================================

interface RouteImage {
  id: string;
  title: string;
  description: string;
  image: string;
}

interface Journey {
  id: string;
  name: string;
  description: string;
  countries: string;
  duration: string;
  price: string;
  image: string;
  badge?: 'Popular' | 'Scenic' | 'Classic';
}

interface Faq {
  q: string;
  a: string;
}

const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1767697358952-4eed45258bf1?w=1800&q=80&fit=crop&crop=center',
  atmosphere: 'https://images.unsplash.com/photo-1562078809-7ef447ae26c6?w=1600&q=80&fit=crop&crop=center',
  alps: 'https://images.unsplash.com/photo-1767697358952-4eed45258bf1?w=1200&q=80&fit=crop&crop=center',
  riviera: 'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=1200&q=80&fit=crop&crop=center',
  prague: 'https://images.unsplash.com/photo-1764094168723-9d2d2c65011d?w=1200&q=80&fit=crop&crop=center',
  paris: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1200&q=80&fit=crop&crop=center',
  onboard: 'https://images.unsplash.com/photo-1523351445-626ec3a80d21?w=1200&q=80&fit=crop&crop=center',
};

const routeImages: RouteImage[] = [
  {
    id: 'alps',
    title: 'The Swiss Alps',
    description: "Glacier-fed valleys and the Matterhorn framed by your carriage window — no climbing gear required.",
    image: IMAGES.alps,
  },
  {
    id: 'riviera',
    title: 'The Italian Riviera',
    description: "Cinque Terre's cliffside villages, linked by one of the most scenic short rail lines in Europe.",
    image: IMAGES.riviera,
  },
  {
    id: 'prague',
    title: 'Prague & Central Europe',
    description: 'Spires and red rooftops, reached by overnight sleeper from half a dozen major cities.',
    image: IMAGES.prague,
  },
  {
    id: 'paris',
    title: 'Paris, by High-Speed Rail',
    description: 'City centre to city centre in a few hours, no airport transfer eating into your day.',
    image: IMAGES.paris,
  },
];

const journeys: Journey[] = [
  {
    id: 'alpine-panorama',
    name: 'Alpine Panorama',
    description: 'Zurich to Zermatt aboard the Glacier Express, with two nights under the Matterhorn to catch it at sunrise.',
    countries: 'Switzerland',
    duration: '5 days / 4 nights',
    price: 'From OMR 480',
    image: IMAGES.alps,
    badge: 'Scenic',
  },
  {
    id: 'riviera-express',
    name: 'Riviera Express',
    description: 'Nice along the coast into Cinque Terre, with local trains connecting all five villages by rail.',
    countries: 'France · Italy',
    duration: '6 days / 5 nights',
    price: 'From OMR 520',
    image: IMAGES.riviera,
    badge: 'Popular',
  },
  {
    id: 'imperial-cities',
    name: 'Imperial Cities',
    description: 'Vienna, Prague, and Budapest, linked by comfortable daytime trains with a night in each city.',
    countries: 'Austria · Czechia · Hungary',
    duration: '7 days / 6 nights',
    price: 'From OMR 610',
    image: IMAGES.prague,
    badge: 'Classic',
  },
  {
    id: 'paris-getaway',
    name: 'Paris Getaway',
    description: 'A short, high-speed hop into the city centre — ideal paired with a London or Brussels stay either side.',
    countries: 'France',
    duration: '3 days / 2 nights',
    price: 'From OMR 310',
    image: IMAGES.paris,
  },
];

const faqs: Faq[] = [
  { q: 'Is a rail pass always the cheaper option?', a: "Not always — it depends on how many journeys you're making. We compare both and recommend whichever costs less for your actual route." },
  { q: 'What are the luggage rules?', a: "Most European trains allow generous carry-on luggage with no separate check-in, though high-speed lines can have space limits we'll flag in advance." },
  { q: 'Can dates be changed after booking?', a: "Some tickets are flexible and some aren't — we'll tell you which applies before you pay, so there are no surprises." },
  { q: 'Are overnight sleeper trains comfortable for families?', a: 'Yes — most sleeper services offer private cabins with bunks, and we can request family compartments where the route allows it.' },
];

function DuneRule() {
  return (
    <div aria-hidden="true" style={{ lineHeight: 0 }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16" fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5" />
        <path d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22" fill="none" stroke="rgba(41,75,90,.14)" strokeWidth="1" />
      </svg>
    </div>
  );
}

export default function RailEurope() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }
        * { box-sizing: border-box; }
        .visa-page { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        .visa-page h1, .visa-page h2, .visa-page h3 { font-family: 'Playfair Display', serif; }
        .visa-page a:focus-visible, .visa-page button:focus-visible { outline: 2px solid var(--gold-d); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { .visa-page *, .visa-page *::before, .visa-page *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }
        .eyebrow.centered { justify-content: center; }

        .section-title { font-size: clamp(1.7rem, 2.6vw, 2.4rem); font-weight: 500; color: var(--ink); line-height: 1.2; margin: 10px 0 12px; }
        .section-title.centered { text-align: center; }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 560px; }
        .section-sub.centered { margin: 0 auto; text-align: center; }
        .section-pad { padding: 84px 0; }
        @media (max-width: 991px) { .section-pad { padding: 64px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 48px 0; } }
        .bg-cream { background: var(--cream); }
        .bg-white { background: #fff; }
        .bg-teal { background: var(--teal-d); }

        .vbtn-primary { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--teal-d); border-radius: 5px; font-size: 12.5px; font-weight: 700; letter-spacing: .3px; padding: 14px 26px; text-decoration: none; transition: transform .2s, background .2s; box-shadow: 0 10px 28px rgba(211,190,118,.28); border: none; cursor: pointer; }
        .vbtn-primary:hover { background: #e2ce86; transform: translateY(-2px); }
        .vbtn-ghost { display: inline-flex; align-items: center; background: rgba(255,255,255,.1); backdrop-filter: blur(4px); color: #fff; border: 1px solid rgba(255,255,255,.25); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 14px 24px; text-decoration: none; transition: background .2s, border-color .2s; }
        .vbtn-ghost:hover { border-color: #fff; background: rgba(255,255,255,.18); }
        .vbtn-outline { display: inline-flex; align-items: center; justify-content: center; gap: 8px; background: transparent; color: var(--teal-d); border: 1.5px solid var(--gold); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 12px 22px; text-decoration: none; transition: background .2s, transform .2s; cursor: pointer; width: 100%; }
        .vbtn-outline:hover { background: var(--gold); transform: translateY(-2px); }

        /* HERO */
        .rail-hero { position: relative; min-height: 560px; display: flex; align-items: center; overflow: hidden; background: var(--teal-d); }
        .rail-hero-img { position: absolute; inset: 0; background-size: cover; background-position: center 65%; opacity: 0.42; }
        .rail-hero-veil { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(26,49,61,.5) 0%, rgba(26,49,61,.92) 100%); }
        .rail-hero-inner { position: relative; z-index: 2; padding: 64px 0 56px; }
        .rail-breadcrumb { font-size: 11.5px; color: rgba(255,255,255,.55); margin-bottom: 18px; }
        .rail-breadcrumb a { color: rgba(255,255,255,.75); text-decoration: none; }
        .rail-breadcrumb a:hover { color: var(--gold-l); }
        .rail-hero-title { font-weight: 600; color: #fff; line-height: 1.16; font-size: clamp(32px, 4.2vw, 52px); max-width: 720px; margin-bottom: 16px; }
        .rail-hero-title .gold { font-style: italic; color: var(--gold); font-weight: 400; }
        .rail-hero-sub { font-size: 15px; color: rgba(255,255,255,.78); font-weight: 300; line-height: 1.75; max-width: 540px; margin-bottom: 30px; }
        .rail-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }

        /* ATMOSPHERE */
        .atmosphere-block {
          position: relative; border-radius: var(--radius); overflow: hidden; min-height: 380px;
          background-size: cover; background-position: center;
          display: flex; align-items: center; justify-content: center; padding: 60px 40px;
        }
        .atmosphere-block .overlay { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(26,49,61,.8) 0%, rgba(26,49,61,.92) 100%); }
        .atmosphere-block .content { position: relative; z-index: 2; text-align: center; max-width: 720px; }
        .atmosphere-block .content h2 { color: #fff; font-size: clamp(1.6rem, 2.6vw, 2.4rem); font-weight: 500; margin-bottom: 12px; }
        .atmosphere-block .content p { color: rgba(255,255,255,.85); font-size: 15px; font-weight: 300; line-height: 1.8; }

        /* ROUTE GALLERY */
        .gallery-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 22px; }
        @media (max-width: 992px) { .gallery-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 560px) { .gallery-grid { grid-template-columns: 1fr; } }
        .gallery-item {
          position: relative; border-radius: var(--radius); overflow: hidden; min-height: 320px;
          background-size: cover; background-position: center; box-shadow: var(--shadow-sm);
          transition: transform .4s cubic-bezier(.22,1,.36,1), box-shadow .4s cubic-bezier(.22,1,.36,1);
          border: 1px solid rgba(41,75,90,.06);
        }
        .gallery-item:hover { transform: translateY(-6px) scale(1.01); box-shadow: var(--shadow-lg); }
        .gallery-item .g-overlay { position: absolute; inset: 0; background: linear-gradient(0deg, rgba(26,49,61,.88) 0%, rgba(26,49,61,.15) 62%); }
        .gallery-item .g-content { position: absolute; bottom: 0; left: 0; right: 0; padding: 22px 20px; z-index: 2; }
        .gallery-item .g-content h3 { color: #fff; font-size: 1.05rem; font-weight: 600; margin-bottom: 4px; }
        .gallery-item .g-content p { color: rgba(255,255,255,.8); font-size: 12px; font-weight: 300; line-height: 1.55; margin: 0; }

        /* JOURNEYS / PACKAGE CARDS */
        .package-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; align-items: stretch; }
        @media (max-width: 1100px) { .package-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 620px) { .package-grid { grid-template-columns: 1fr; } }
        .package-card {
          background: #fff; border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow-sm);
          border: 1px solid rgba(41,75,90,.06); transition: transform .4s cubic-bezier(.22,1,.36,1), box-shadow .4s cubic-bezier(.22,1,.36,1);
          display: flex; flex-direction: column; position: relative;
        }
        .package-card:hover { transform: translateY(-8px); box-shadow: var(--shadow-lg); }
        .package-card .badge {
          position: absolute; top: 14px; right: 14px; z-index: 2; background: var(--gold); color: var(--teal-d);
          font-size: 9.5px; font-weight: 700; letter-spacing: .5px; padding: 4px 12px; border-radius: 20px; text-transform: uppercase;
        }
        .package-card .badge.popular { background: var(--teal); color: #fff; }
        .package-card-img { height: 170px; background-size: cover; background-position: center; }
        .package-card-body { padding: 20px 20px 24px; flex: 1; display: flex; flex-direction: column; }
        .package-card-body h3 { font-size: 1.05rem; font-weight: 600; color: var(--ink); margin-bottom: 2px; }
        .package-card-body .countries { font-size: 11.5px; color: var(--gold-d); font-weight: 600; letter-spacing: .3px; display: block; margin-bottom: 4px; }
        .package-card-body .duration { font-size: 11.5px; color: var(--silver); font-weight: 400; margin-bottom: 6px; display: block; }
        .package-card-body .price { font-family: 'Playfair Display', serif; font-size: 1.15rem; font-weight: 600; color: var(--gold-d); margin: 4px 0 8px; }
        .package-card-body p { font-size: 12.5px; color: var(--silver); font-weight: 300; line-height: 1.6; margin-bottom: 14px; flex: 1; }

        /* ONBOARD COMFORT SPLIT PANEL */
        .svc-split { display: grid; grid-template-columns: 1fr 1fr; border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow-md); border: 1px solid rgba(41,75,90,.09); background: #fff; }
        @media (max-width: 900px) { .svc-split { grid-template-columns: 1fr; } }
        .svc-split-media { position: relative; min-height: 320px; background-size: cover; background-position: center; }
        @media (min-width: 901px) { .svc-split-media { min-height: 100%; } }
        .svc-split-content { padding: 44px 42px; display: flex; flex-direction: column; justify-content: center; }
        .svc-split-title { font-size: clamp(1.4rem, 2vw, 1.9rem); font-weight: 600; color: var(--ink); line-height: 1.2; margin: 10px 0 14px; }
        .svc-split-text { font-size: 14.5px; color: var(--silver); font-weight: 300; line-height: 1.75; margin-bottom: 22px; }
        .split-list { list-style: none; padding: 0; margin: 0; }
        .split-list li { font-size: 13px; color: var(--teal); font-weight: 400; padding: 5px 0; display: flex; align-items: center; gap: 9px; }
        .split-list li::before { content: '✓'; color: var(--gold-d); font-weight: 700; }

        /* FAQ */
        .faq-list { max-width: 760px; margin: 0 auto; }
        .faq-item { border-bottom: 1px solid rgba(41,75,90,.1); }
        .faq-item:first-child { border-top: 1px solid rgba(41,75,90,.1); }
        .faq-q {
          width: 100%; background: none; border: none; text-align: left; cursor: pointer;
          display: flex; align-items: center; justify-content: space-between; gap: 20px;
          padding: 22px 4px; font-family: 'Playfair Display', serif; font-size: 1.02rem; font-weight: 500; color: var(--ink);
        }
        .faq-q .plus { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid var(--gold-d); position: relative; transition: transform .3s ease, background .3s ease; }
        .faq-q .plus::before, .faq-q .plus::after { content: ''; position: absolute; background: var(--gold-d); top: 50%; left: 50%; transform: translate(-50%,-50%); }
        .faq-q .plus::before { width: 9px; height: 1.4px; }
        .faq-q .plus::after { width: 1.4px; height: 9px; transition: opacity .2s ease; }
        .faq-item.open .plus { background: var(--gold-d); transform: rotate(180deg); }
        .faq-item.open .plus::before, .faq-item.open .plus::after { background: #fff; }
        .faq-item.open .plus::after { opacity: 0; }
        .faq-a { max-height: 0; overflow: hidden; transition: max-height .35s ease; }
        .faq-item.open .faq-a { max-height: 240px; }
        .faq-a p { font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.75; padding: 0 4px 24px; max-width: 640px; }

        /* CTA BANNER */
        .cta-banner { background: var(--teal); border-radius: var(--radius); padding: 44px 40px; display: flex; align-items: center; justify-content: space-between; gap: 30px; flex-wrap: wrap; }
        .cta-banner-title { color: #fff; font-size: 1.3rem; font-weight: 500; margin-bottom: 4px; }
        .cta-banner-sub { color: rgba(255,255,255,.7); font-size: 13px; font-weight: 300; max-width: 460px; }

        @media (max-width: 576px) {
          .cta-banner { padding: 28px 20px; }
          .atmosphere-block { padding: 40px 24px; min-height: 280px; }
          .gallery-item { min-height: 220px; }
        }
`}</style>

      <div className="visa-page">
        <Header />

        {/* ===== HERO ===== */}
        <section className="rail-hero">
          <div className="rail-hero-img" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
          <div className="rail-hero-veil" />
          <div className="container rail-hero-inner">
            <div className="rail-breadcrumb"><a href="/services">Services</a> / Rail Europe</div>
            <div className="eyebrow light" style={{ marginBottom: 14 }}>
              Explore Europe by Rail
            </div>
            <h1 className="rail-hero-title">
              Every Window, <span className="gold">a Postcard</span>
            </h1>
            <p className="rail-hero-sub">
              From alpine passes to coastal cliffside villages, Europe's rail network turns the
              journey into part of the holiday. We handle the passes, the reservations, and the
              connections — you just watch it go by.
            </p>
            <div className="rail-hero-ctas">
              <a href="#journeys" className="vbtn-primary">Browse Rail Journeys</a>
              <a href="#routes" className="vbtn-ghost">See the Routes</a>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== ATMOSPHERE ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div className="atmosphere-block" style={{ backgroundImage: `url(${IMAGES.atmosphere})` }}>
              <div className="overlay" />
              <div className="content">
                <span className="eyebrow light" style={{ justifyContent: 'center' }}>
                  The Scenic Way to See a Continent
                </span>
                <h2>No Airport, No Security Line, No Rush</h2>
                <p>
                  Step on in one city centre and step off in another, with mountains, coastlines,
                  and vineyards passing the window in between. We put together the passes and
                  reservations so the only decision left is which seat gets the better view.
                </p>
              </div>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== ROUTE GALLERY ===== */}
        <section className="section-pad bg-cream" id="routes">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Where the Rails Go
              </span>
              <h2 className="section-title centered">Four Regions Worth the Window Seat</h2>
              <p className="section-sub centered">
                A small sample of what's reachable by train alone — no rental car, no internal
                flights, no lost time at a second airport.
              </p>
            </div>
            <div className="gallery-grid">
              {routeImages.map((item) => (
                <div className="gallery-item" key={item.id} style={{ backgroundImage: `url(${item.image})` }}>
                  <div className="g-overlay" />
                  <div className="g-content">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== JOURNEYS ===== */}
        <section className="section-pad bg-white" id="journeys">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Popular Rail Journeys
              </span>
              <h2 className="section-title centered">Ready-Built Routes, Or We'll Draw Your Own</h2>
              <p className="section-sub centered">
                A starting point — every journey below can be shortened, extended, or combined
                with a different region entirely.
              </p>
            </div>
            <div className="package-grid">
              {journeys.map((j) => (
                <div className="package-card" key={j.id}>
                  {j.badge && <span className={`badge ${j.badge.toLowerCase()}`}>{j.badge}</span>}
                  <div className="package-card-img" style={{ backgroundImage: `url(${j.image})` }} />
                  <div className="package-card-body">
                    <h3>{j.name}</h3>
                    <span className="countries">{j.countries}</span>
                    <span className="duration">{j.duration}</span>
                    <div className="price">{j.price}</div>
                    <p>{j.description}</p>
                    <a href="/contact" className="vbtn-outline">Enquire About This Route</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== ONBOARD COMFORT ===== */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div className="svc-split">
              <div className="svc-split-media" style={{ backgroundImage: `url(${IMAGES.onboard})` }} />
              <div className="svc-split-content">
                <span className="eyebrow">Onboard</span>
                <div className="svc-split-title">The seat matters as much as the scenery</div>
                <p className="svc-split-text">
                  A reserved window seat on the right side of the carriage is the difference
                  between watching the Rhine valley slide past and staring at a luggage rack. We
                  request seating deliberately, not as an afterthought.
                </p>
                <ul className="split-list">
                  <li>Window-side reservations requested by default</li>
                  <li>First-class upgrades available on eligible routes</li>
                  <li>Luggage and connection times checked for each transfer</li>
                  <li>E-tickets and seat maps sent before departure</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== FAQ ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 36 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Before You Book
              </span>
              <h2 className="section-title centered">Frequently Asked Questions</h2>
            </div>
            <div className="faq-list">
              {faqs.map((f, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div className={`faq-item${isOpen ? ' open' : ''}`} key={f.q}>
                    <button
                      type="button"
                      className="faq-q"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                    >
                      <span>{f.q}</span>
                      <span className="plus" />
                    </button>
                    <div className="faq-a">
                      <p>{f.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== FINAL CTA ===== */}
        <section className="section-pad bg-cream" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="cta-banner">
              <div>
                <div className="cta-banner-title">Plan Your European Rail Route</div>
                <div className="cta-banner-sub">
                  Send us your cities and dates — we'll find the better value between a pass and
                  individual tickets, and the window seats to go with it.
                </div>
              </div>
              <a href="/contact" className="vbtn-primary">
                Get a Quote
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        <DefaultFooter />
      </div>
    </>
  );
}